"use client";

import { PasswordStrengthIndicator } from "@/app/(front-door)/_components/PasswordStrengthIndicator";
import { FrontDoorNotice } from "@/app/(front-door)/_components/FrontDoorNotice";
import {
  FRONT_DOOR_FIELD_STYLES,
  FRONT_DOOR_HELP_CLASS,
  FRONT_DOOR_LINK_CLASS,
  FRONT_DOOR_PANEL_TITLE_CLASS,
  FRONT_DOOR_PRIMARY_BUTTON_CLASS,
} from "@/app/(front-door)/_components/styles";
import { Button } from "@/app/_components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/app/_components/ui/form";
import { Input } from "@/app/_components/ui/input";
import { PasswordInput } from "@/app/_components/ui/password-input";
import { MarbleLink as Link } from "@/app/_components/ui/marble-link";
import getLogger from "@/lib/logger";
import { EMAIL_IN_USE_MESSAGE } from "@/lib/modules/auth/constants";
import { signUpFromFrontDoorAction } from "@/lib/modules/auth/actions/front-door-sign-up";
import type { FrontDoorSignUpRole } from "@/lib/modules/auth/front-door";
import { AuthSchema, type TAuthSchema } from "@/lib/modules/auth/schemas";
import { isServerActionError } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { signIn } from "next-auth/react";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { useForm } from "react-hook-form";

const logger = getLogger("front-door:staff-sign-up");

/** Today's school worker and pediatrician sign-up, plus its invite states. */
export const StaffSignUpForm = ({
  role,
  schoolId,
  inviteEmail,
  isInviteInvalid,
  onDropInvite,
  callbackUrl,
}: {
  role: FrontDoorSignUpRole;
  schoolId?: number;
  /** From a valid invite link; the email is filled in and locked. */
  inviteEmail?: string;
  isInviteInvalid: boolean;
  /** Leaves an expired invite for the plain sign-up. */
  onDropInvite: () => void;
  callbackUrl?: string;
}) => {
  const router = useRouter();
  const [passwordValue, setPasswordValue] = useState("");
  const form = useForm<TAuthSchema>({
    resolver: zodResolver(AuthSchema),
    defaultValues: { email: inviteEmail ?? "", password: "" },
  });
  // Health plan staff and pediatricians refer members, not students.
  const isPediatric = role === "PEDIATRICIAN" || role === "HEALTH_PLAN";

  const signUpMutation = useMutation({
    mutationFn: async (values: TAuthSchema) => {
      const result = await signUpFromFrontDoorAction(values, {
        role,
        schoolId,
      });
      if (isServerActionError(result)) {
        return { error: result.error };
      }
      const response = await signIn("credentials", {
        ...values,
        redirect: false,
      });
      return { isSignedIn: !!response?.ok, signInError: response?.error };
    },
    onSuccess: (result) => {
      if ("error" in result) {
        if (result.error === EMAIL_IN_USE_MESSAGE) {
          form.setError("email", { type: "custom", message: result.error });
        } else {
          form.setError("root", { type: "custom", message: result.error });
        }
        return;
      }
      if (result.isSignedIn) {
        router.push(callbackUrl ?? "/");
        router.refresh();
        return;
      }
      logger.error(result, "Error signing in after sign-up");
      form.setError("root", {
        type: "custom",
        message: "Your account is ready. Please sign in from the Sign in tab.",
      });
    },
    onError: (error) => {
      logger.error(error, "Error signing up user");
      form.setError("root", {
        type: "custom",
        message: "Something went wrong. Please reload and try again.",
      });
    },
  });

  if (isInviteInvalid) {
    return (
      <div>
        <h2 className={FRONT_DOOR_PANEL_TITLE_CLASS}>Invite link expired</h2>
        <p className={`mb-[22px] mt-2 text-sm ${FRONT_DOOR_HELP_CLASS}`}>
          This invite link is no longer valid.
        </p>
        <FrontDoorNotice tone="error">
          {
            "Please reach out to the person who invited you to request a new invite link, or "
          }
          <button
            type="button"
            onClick={onDropInvite}
            className={`font-semibold ${FRONT_DOOR_LINK_CLASS}`}
          >
            create a new account here
          </button>
          .
        </FrontDoorNotice>
      </div>
    );
  }

  const isInvite = inviteEmail !== undefined;
  const isSignedIn =
    !!signUpMutation.data &&
    "isSignedIn" in signUpMutation.data &&
    signUpMutation.data.isSignedIn;

  return (
    <div>
      <h2 className={FRONT_DOOR_PANEL_TITLE_CLASS}>
        {isInvite ? "Set up your Marble Health account" : "Welcome to Marble!"}
      </h2>
      <p className={`mb-[22px] mt-2 text-sm ${FRONT_DOOR_HELP_CLASS}`}>
        {_getSignUpLead({ isInvite, isPediatric })}
      </p>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit((values) =>
            signUpMutation.mutate(values),
          )}
          method="post"
          className={FRONT_DOOR_FIELD_STYLES}
        >
          {form.formState.errors.root && (
            <FrontDoorNotice tone="error" className="mb-[18px]">
              {form.formState.errors.root.message}
            </FrontDoorNotice>
          )}
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type="email"
                    autoComplete="email"
                    placeholder={isPediatric ? "Email" : "School email address"}
                    disabled={isInvite}
                  />
                </FormControl>
                {!isPediatric && !isInvite && (
                  <FormDescription className={FRONT_DOOR_HELP_CLASS}>
                    Please use your school email address.
                  </FormDescription>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem className="mt-[18px]">
                <FormLabel>Password</FormLabel>
                <FormControl>
                  <PasswordInput
                    {...field}
                    autoComplete="new-password"
                    placeholder="Create a password"
                    onChange={(event) => {
                      field.onChange(event);
                      setPasswordValue(event.target.value);
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <PasswordStrengthIndicator passwordValue={passwordValue} />
          <Button
            type="submit"
            variant="custom"
            className={`mt-[22px] ${FRONT_DOOR_PRIMARY_BUTTON_CLASS}`}
            loading={signUpMutation.isPending || isSignedIn}
          >
            Sign up
          </Button>
          <p className={`mt-3.5 text-center ${FRONT_DOOR_HELP_CLASS}`}>
            {"By signing up, you agree to our "}
            <Link
              href="/terms"
              target="_blank"
              rel="noopener noreferrer"
              className={FRONT_DOOR_LINK_CLASS}
            >
              Terms of Service
            </Link>
            .
          </p>
        </form>
      </Form>
    </div>
  );
};

const _getSignUpLead = ({
  isInvite,
  isPediatric,
}: {
  isInvite: boolean;
  isPediatric: boolean;
}) => {
  if (isInvite) {
    return "You've been invited to Marble. Create a password to finish setting up your account.";
  }
  return isPediatric
    ? "Create a free account to refer members to care. It takes 2 minutes."
    : "Create a free account to refer students to care and get access to our school resources. It takes 2 minutes.";
};
